import { TextButton } from '@shipt/design-system-buttons'
import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { useRef, type Dispatch, type SetStateAction } from 'react'
import styled from 'styled-components'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicProductInstructionsModal } from '@/features/shop/components/Modal/ProductInstructionsModal/DynamicProductInstructionsModal'
import { useMutationUpdateCart } from '@/features/shop/services/Cart/mutations'
import dynamic from 'next/dynamic'
import { useMutationUpdateOrder } from '@/features/shop/services/Orders/mutations'
import { useActiveOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { useBasket } from '@/features/shared/hooks/useBasket'

const QuickAddNotePopover = dynamic(
  () =>
    import('@/features/shop/components/ProductQuantityQuickAdd/QuickAddNotePopover').then(
      (mod) => mod.QuickAddNotePopover,
    ),
  { ssr: false },
)

type Props = {
  product: ProductCardProduct
  className?: string
  shouldShowPopover: boolean
  setIsOpen?: Dispatch<SetStateAction<boolean>>
}

export const ProductQuickAddNote = ({
  product,
  className,
  shouldShowPopover,
  setIsOpen,
}: Props) => {
  const { openDialog } = useHandleDialog()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const willSeeNoteTooltip = useRef(shouldShowPopover)
  const { getProductNote } = useBasket()
  const activeOrder = useActiveOrder()
  const { mutateAsync: updateOrder } = useMutationUpdateOrder()
  const { mutateAsync: updateCart } = useMutationUpdateCart()

  const handleSubmit = (note: string) => {
    if (activeOrder) {
      updateOrder({
        order: activeOrder,
        products: { [product.id]: { ...product, note } },
      })
    } else {
      updateCart({ ...product, note })
    }
  }

  const handleOpenProductInstructionsModal = () => {
    setIsOpen?.(false)
    trackElementClicked({
      link_name: 'add note or backup',
      display_format: 'product_notes',
      recommended: willSeeNoteTooltip.current,
    })
    openDialog(DynamicProductInstructionsModal, {
      product: { ...product, note: getProductNote(product) },
      handleSubmit,
    })
  }

  return (
    <div className={className}>
      {!!shouldShowPopover && <QuickAddNotePopover referenceRef={buttonRef} />}
      <AddNoteButton
        ref={buttonRef}
        size="md"
        onClick={handleOpenProductInstructionsModal}
        aria-label="Select backup product. Do you have any notes for your shopper?"
        aria-live="off"
        surface="inverse"
      >
        Select backup
      </AddNoteButton>
    </div>
  )
}

const AddNoteButton = styled(TextButton)`
  justify-content: center;
  width: 100%;
  padding: 0.5rem 0;
`
